import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserInfo } from '../../../types/verification';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext'; // Added import

type Props = {
  userInfo: UserInfo;
  setUserInfo: (info: UserInfo) => void;
  onNext: () => void;
  onBack: () => void;
};

type ValidationErrors = {
  [K in keyof UserInfo]?: string;
};

export const InfoStep = ({ userInfo, setUserInfo, onNext, onBack }: Props) => {
  const { translations } = useLanguage(); // Added hook
  const [touched, setTouched] = useState<{ [K in keyof UserInfo]?: boolean }>({});
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validateField = (field: keyof UserInfo, value: string | undefined): string => {
    // Handle undefined values
    if (value === undefined) {
      // For optional fields, return empty string
      if (field === 'birthDate') return '';
      // For required fields, return required error
      return 'This field is required';
    }

    switch (field) {
      case 'fullName':
        return value.length < 3
          ? translations?.infoStep?.errors?.fullName || 'Full name must be at least 3 characters'
          : '';
      case 'passportNumber':
        return !/^[A-Z0-9]{6,12}$/i.test(value)
          ? translations?.infoStep?.errors?.passportNumber || 'Invalid passport number format'
          : '';
      case 'passportIssuanceDate':
        if (!value) return translations?.infoStep?.errors?.issuanceDateRequired || 'Issuance date is required';
        const issuanceDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time part for accurate date comparison

        if (isNaN(issuanceDate.getTime())) {
          return translations?.infoStep?.errors?.invalidDate || 'Invalid date format';
        }
        if (issuanceDate > today) {
          return translations?.infoStep?.errors?.futureDate || 'Issuance date cannot be in the future';
        }
        // Optional: Add minimum date validation (e.g., not older than 10 years)
        const tenYearsAgo = new Date();
        tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
        if (issuanceDate < tenYearsAgo) {
          return translations?.infoStep?.errors?.oldDate || 'Passport issuance date cannot be older than 10 years';
        }
        return '';
      case 'birthDate':
        if (!value) return ''; // Birth date is optional
        const birthDate = new Date(value);
        const now = new Date();

        if (isNaN(birthDate.getTime())) {
          return translations?.infoStep?.errors?.invalidDate || 'Invalid date format';
        }
        if (birthDate > now) {
          return translations?.infoStep?.errors?.futureDate || 'Birth date cannot be in the future';
        }
        return '';
      case 'nationality':
        return !value
          ? translations?.infoStep?.errors?.nationalityRequired || 'Nationality is required'
          : '';
      case 'country':
        return !value
          ? translations?.infoStep?.errors?.countryRequired || 'Country is required'
          : '';
      case 'mobileNumber':
        return !/^\+?[1-9]\d{7,14}$/.test(value)
          ? translations?.infoStep?.errors?.invalidMobile || 'Invalid mobile number format'
          : '';
      case 'email':
        return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? translations?.infoStep?.errors?.invalidEmail || 'Invalid email format'
          : '';
      case 'agent':
        return value.length < 2
          ? translations?.infoStep?.errors?.agentRequired || 'Agent/Company name is required'
          : '';
      default:
        return '';
    }
  };

  const handleChange = (field: keyof UserInfo, value: string) => {
    const newUserInfo = { ...userInfo, [field]: value };
    setUserInfo(newUserInfo);

    // Validate field
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleBlur = (field: keyof UserInfo) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, userInfo[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const isFormValid = () => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    (Object.keys(userInfo) as Array<keyof UserInfo>).forEach(field => {
      const error = validateField(field, userInfo[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    // Mark all fields as touched
    const allTouched = (Object.keys(userInfo) as Array<keyof UserInfo>).reduce(
      (acc, field) => ({ ...acc, [field]: true }),
      {}
    );
    setTouched(allTouched);

    if (isFormValid()) {
      onNext();
    }
  };

  const renderInput = (
    field: keyof UserInfo,
    label: string,
    type: string = 'text',
    placeholder: string = '',
    showValidation: boolean = true,
    dateConstraints?: { min?: string; max?: string }
  ) => {
    const hasError = touched[field] && errors[field];
    const isValid = touched[field] && !errors[field];

    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="relative">
          <input
            type={type}
            value={userInfo[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={() => handleBlur(field)}
            placeholder={placeholder}
            {...(dateConstraints || {})}
            className={`
              w-full px-4 py-2 rounded-lg border transition-colors
              ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              ${isValid ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : ''}
              ${!touched[field] ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500' : ''}
            `}
          />
          {hasError && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <AlertCircle className="text-red-500" size={20} />
            </div>
          )}
          {isValid && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <CheckCircle className="text-green-500" size={20} />
            </div>
          )}
        </div>
        {hasError && (
          <p className="mt-1 text-sm text-red-500">{errors[field]}</p>
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h2 className="text-2xl font-bold">
        {translations?.infoStep?.title || 'Personal Information'}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderInput(
          'fullName',
          translations?.infoStep?.labels?.fullName || 'Full Name (as in passport)',
          'text',
          translations?.infoStep?.placeholders?.fullName || 'Enter your full name'
        )}
        {renderInput(
          'passportNumber',
          translations?.infoStep?.labels?.passportNumber || 'Passport Number',
          'text',
          translations?.infoStep?.placeholders?.passportNumber || 'Enter passport number'
        )}
        {renderInput(
          'passportIssuanceDate',
          translations?.infoStep?.labels?.passportIssuanceDate || 'Passport Issuance Date',
          'date',
          '',
          true,
          {
            max: new Date().toISOString().split('T')[0], // Set max date to today
            min: new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0] // Set min date to 10 years ago
          }
        )}
        {renderInput(
          'birthDate',
          translations?.infoStep?.labels?.birthDate || 'Birth Date',
          'date',
          '',
          true,
          {
            max: new Date().toISOString().split('T')[0], // Set max date to today
          }
        )}
        {renderInput(
          'nationality',
          translations?.infoStep?.labels?.nationality || 'Nationality',
          'text',
          translations?.infoStep?.placeholders?.nationality || 'Enter your nationality'
        )}
        {renderInput(
          'country',
          translations?.infoStep?.labels?.country || 'Country of Residence',
          'text',
          translations?.infoStep?.placeholders?.country || 'Enter your country'
        )}
        {renderInput(
          'mobileNumber',
          translations?.infoStep?.labels?.mobileNumber || 'Mobile Number',
          'tel',
          translations?.infoStep?.placeholders?.mobileNumber || '+1234567890'
        )}
        {renderInput(
          'email',
          translations?.infoStep?.labels?.email || 'Email Address',
          'email',
          translations?.infoStep?.placeholders?.email || 'example@domain.com'
        )}
        {renderInput(
          'agent',
          translations?.infoStep?.labels?.agent || 'Agent/Company Name',
          'text',
          translations?.infoStep?.placeholders?.agent || 'Enter company name'
        )}
      </div>

      <div className="flex justify-between pt-6">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBack}
          className="px-6 py-2 text-primary-500 border border-primary-500 rounded-lg hover:bg-primary-50 transition-colors"
        >
          {translations?.infoStep?.buttons?.back || 'Back'}
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSubmit}
          className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          {translations?.infoStep?.buttons?.next || 'Next'}
        </motion.button>
      </div>
    </motion.div>
  );
};